import {
    BookOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    FileTextOutlined,
    FormOutlined,
    HistoryOutlined,
    ReloadOutlined,
    SearchOutlined,
    TeamOutlined,
    TrophyOutlined,
    UnorderedListOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { Button, Col, DatePicker, Input, InputNumber, Row, Space, Table, Tabs, Tag } from 'antd';
import Column from 'antd/es/table/Column';
import viVN from 'antd/lib/date-picker/locale/vi_VN';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ContentHeader } from '../../components';
import { currencyFormat, dateCommon } from '../../helpers/common';
import TrangThaiLopDaoTaoTag from './TrangThaiLopDaoTaoTag';
import {
    layDSChungChiSapHetHan,
    layDSNhanVienChuaHoanThanhDaoTao,
    layLichSuDaoTaoTheoNhanVien,
    layThongKeDaoTaoTheoDonVi,
    layTongHopDaoTao,
} from '../../services/daotao';

const { RangePicker } = DatePicker;

const kyOptions = [
    { label: '1 tháng', months: 1 },
    { label: '6 tháng', months: 6 },
    { label: '9 tháng', months: 9 },
    { label: 'Cả năm', months: 12 },
];

/* ── Stat card modern & clean ────────────────────────────── */
const StatCard = ({
    label,
    value,
    color,
    icon,
    suffix = '',
}: {
    label: string;
    value: string | number;
    color: string;
    icon: React.ReactNode;
    suffix?: string;
}) => (
    <div
        style={{
            background: '#fff',
            borderRadius: 10,
            padding: '16px 18px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            border: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            height: '100%',
            transition: 'all 0.25s ease',
        }}
    >
        <div
            style={{
                width: 46,
                height: 46,
                borderRadius: 10,
                backgroundColor: `${color}15`,
                color: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                flexShrink: 0,
            }}
        >
            {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 4, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {label}
            </div>
            <div style={{ fontSize: typeof value === 'string' && value.length > 13 ? 17 : 20, fontWeight: 700, color: '#1f1f1f', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
                {suffix && <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 2, color }}>{suffix}</span>}
            </div>
        </div>
    </div>
);

/* ── Helper: group dữ liệu lớp đào tạo chưa hoàn thành ──────── */
const groupLopChuaHoanThanh = (data: any[]) => {
    const map = new Map<number, any>();
    data.forEach((row) => {
        if (!map.has(row.idLopDaoTao)) {
            map.set(row.idLopDaoTao, {
                key: row.idLopDaoTao,
                idLopDaoTao: row.idLopDaoTao,
                tenLopDaoTao: row.tenLopDaoTao,
                ngayBatDau: row.ngayBatDau,
                ngayKetThuc: row.ngayKetThuc,
                giangVien: row.giangVien,
                donViDaoTao: row.donViDaoTao,
                tenHinhThucToChuc: row.tenHinhThucToChuc,
                trangThaiLop: row.trangThaiLop,
                dsNhanVien: [],
            });
        }
        map.get(row.idLopDaoTao).dsNhanVien.push(row);
    });
    return Array.from(map.values()).map((lop) => ({
        ...lop,
        soNhanVienChuaHoanThanh: lop.dsNhanVien.length,
    }));
};

/* ── Helper: group dữ liệu lịch sử theo nhân viên ───────────── */
const groupLichSu = (data: any[]) => {
    const map = new Map<number, any>();
    data.forEach((row) => {
        if (!map.has(row.idNhanVien)) {
            map.set(row.idNhanVien, {
                key: row.idNhanVien,
                idNhanVien: row.idNhanVien,
                hoTen: row.hoTen,
                tenDonVi: row.tenDonVi,
                tenNhomNhanSu: row.tenNhomNhanSu,
                tenChucVu: row.tenChucVu,
                lopDaoTao: [],
            });
        }
        map.get(row.idNhanVien).lopDaoTao.push(row);
    });
    return Array.from(map.values()).map((nv) => ({
        ...nv,
        soLopThamGia: nv.lopDaoTao.length,
    }));
};

/* ── Filter bar dùng chung cho tab 1 & 2 ──────────────────── */
const FilterBar = ({
    tuNgay,
    denNgay,
    loading,
    onKy,
    onRange,
    onReload,
}: any) => (
    <Row className="form-group" gutter={[8, 8]} align="middle">
        <Col>
            <Space size={4}>
                {kyOptions.map((ky) => (
                    <Button size="small" key={ky.months} onClick={() => onKy(ky.months)}>
                        {ky.label}
                    </Button>
                ))}
            </Space>
        </Col>
        <Col>
            <RangePicker
                size="small"
                locale={viVN}
                format="DD/MM/YYYY"
                value={tuNgay && denNgay ? [dayjs(tuNgay), dayjs(denNgay)] : null}
                onChange={(dates) => {
                    if (dates) {
                        onRange(dates[0]?.toISOString(), dates[1]?.toISOString());
                    } else {
                        onRange(undefined, undefined);
                    }
                }}
            />
        </Col>
        {onReload && (
            <Col>
                <Button
                    size="small"
                    icon={<ReloadOutlined rev="" />}
                    onClick={onReload}
                    loading={loading}
                >
                    Làm mới
                </Button>
            </Col>
        )}
    </Row>
);

/* ════════════════════════════════════════════════════════════ */
const BaoCaoDaoTao = () => {
    const [tuNgay, setTuNgay] = useState<string | undefined>(undefined);
    const [denNgay, setDenNgay] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [tongHop, setTongHop] = useState<any>(null);
    const [dsTheoDonVi, setDsTheoDonVi] = useState<any[]>([]);
    const [dsChuaHoanThanh, setDsChuaHoanThanh] = useState<any[]>([]);
    const [dsLopChuaHoanThanhGrouped, setDsLopChuaHoanThanhGrouped] = useState<any[]>([]);

    const [soNgayChungChi, setSoNgayChungChi] = useState<number>(30);
    const [dsChungChiSapHetHan, setDsChungChiSapHetHan] = useState<any[]>([]);
    const [loadingChungChi, setLoadingChungChi] = useState(false);

    const [tuKhoaLichSu, setTuKhoaLichSu] = useState('');
    const [dsLichSuGrouped, setDsLichSuGrouped] = useState<any[]>([]);
    const [loadingLichSu, setLoadingLichSu] = useState(false);
    const [expandedLichSu, setExpandedLichSu] = useState<any[]>([]);

    const apDungKy = (months: number) => {
        const tu = dayjs().subtract(months, 'month').startOf('month');
        const den = dayjs().endOf('month');
        setTuNgay(tu.toISOString());
        setDenNgay(den.toISOString());
    };

    const loadBaoCao = async (tu?: string, den?: string) => {
        setLoading(true);
        try {
            const [tongHopData, donViData, chuaHoanThanhData] = await Promise.all([
                layTongHopDaoTao(tu, den),
                layThongKeDaoTaoTheoDonVi(tu, den),
                layDSNhanVienChuaHoanThanhDaoTao(tu, den),
            ]);
            setTongHop(tongHopData);
            setDsTheoDonVi((donViData || []).map((item: any, i: number) => ({ ...item, key: i })));
            setDsChuaHoanThanh((chuaHoanThanhData || []).map((item: any, i: number) => ({ ...item, key: i })));
            setDsLopChuaHoanThanhGrouped(groupLopChuaHoanThanh(chuaHoanThanhData || []));
        } catch (err: any) {
            toast.error(err?.message || 'Có lỗi xảy ra');
        } finally {
            setLoading(false);
        }
    };

    const loadChungChi = async () => {
        setLoadingChungChi(true);
        try {
            const data = await layDSChungChiSapHetHan(soNgayChungChi);
            setDsChungChiSapHetHan((data || []).map((item: any, i: number) => ({ ...item, key: i })));
        } catch (err: any) {
            toast.error(err?.message || 'Có lỗi xảy ra');
        } finally {
            setLoadingChungChi(false);
        }
    };

    const loadLichSu = async () => {
        setLoadingLichSu(true);
        try {
            const data = await layLichSuDaoTaoTheoNhanVien(undefined, tuKhoaLichSu);
            setDsLichSuGrouped(groupLichSu(data || []));
            setExpandedLichSu([]);
        } catch (err: any) {
            toast.error(err?.message || 'Có lỗi xảy ra');
        } finally {
            setLoadingLichSu(false);
        }
    };

    useEffect(() => {
        loadBaoCao(tuNgay, denNgay);
    }, [tuNgay, denNgay]);

    useEffect(() => {
        loadChungChi();
        loadLichSu();
    }, []);

    /* ── Bảng lớp học bên trong expand ─── */
    const expandedLopDaoTao = (record: any) => (
        <Table
            dataSource={(record.lopDaoTao || []).map((r: any, i: number) => ({ ...r, key: i }))}
            pagination={false}
            size="small"
            style={{ margin: '4px 0' }}
        >
            <Column title="STT" render={(_: any, __: any, idx: number) => idx + 1} width={50} />
            <Column title="Tên lớp đào tạo" dataIndex="tenLopDaoTao" />
            <Column
                title="Ngày bắt đầu"
                dataIndex="ngayBatDau"
                width={110}
                render={(v: string) => (v ? dateCommon.formatDate(v) : '')}
            />
            <Column
                title="Ngày kết thúc"
                dataIndex="ngayKetThuc"
                width={110}
                render={(v: string) => (v ? dateCommon.formatDate(v) : '')}
            />
            <Column title="Giảng viên" dataIndex="giangVien" />
            <Column title="Hình thức" dataIndex="tenHinhThucToChuc" />
            <Column
                title="Học phí"
                dataIndex="hocPhi"
                align="right"
                width={110}
                render={(v: number, record: any) => {
                    const isNoiBo = record.idHinhThucToChuc === 2 || record.tenHinhThucToChuc === 'Nội bộ';
                    return isNoiBo ? '-' : currencyFormat.toVND(v);
                }}
            />
            <Column title="Kết quả" dataIndex="ketQuaDaoTao" width={100} />
            <Column title="Điểm" dataIndex="diemDanhGia" align="center" width={70} />
            <Column
                title="Chứng chỉ"
                dataIndex="isCapChungChi"
                align="center"
                width={90}
                render={(v: boolean) =>
                    v ? <Tag color="green">Đã cấp</Tag> : <Tag color="default">Không</Tag>
                }
            />
        </Table>
    );

    /* ── Bảng nhân viên chưa hoàn thành bên trong expand của lớp ─── */
    const expandedNhanVienChuaHoanThanh = (record: any) => (
        <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: 8, border: '1px solid #e2e8f0', margin: '4px 0' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
                Danh sách nhân viên chưa hoàn thành ({record.dsNhanVien?.length || 0} nhân viên):
            </div>
            <Table
                dataSource={(record.dsNhanVien || []).map((r: any, i: number) => ({ ...r, key: i }))}
                pagination={false}
                size="small"
            >
                <Column title="STT" render={(_: any, __: any, idx: number) => idx + 1} width={50} align="center" />
                <Column
                    title="Họ và tên"
                    dataIndex="hoTen"
                    render={(v: string, r: any) => (
                        <span>
                            <strong>{v}</strong>
                            {r.maNhanVien ? <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>({r.maNhanVien})</span> : null}
                        </span>
                    )}
                />
                <Column title="Phòng ban / Đơn vị" dataIndex="tenDonVi" />
                <Column title="Chức vụ" dataIndex="tenChucVu" />
                <Column
                    title="Điểm đánh giá"
                    dataIndex="diemDanhGia"
                    align="center"
                    width={130}
                    render={(v: string) =>
                        v && v.trim() !== '' ? (
                            <Tag color="blue">{v}</Tag>
                        ) : (
                            <Tag color="default">Chưa có điểm</Tag>
                        )
                    }
                />
                <Column
                    title="Kết quả đào tạo"
                    dataIndex="ketQuaDaoTao"
                    align="center"
                    width={140}
                    render={(v: string) =>
                        v && v.trim() !== '' ? (
                            <Tag color="cyan">{v}</Tag>
                        ) : (
                            <Tag color="default">Chưa có kết quả</Tag>
                        )
                    }
                />
                <Column
                    title="Chứng chỉ"
                    dataIndex="isCapChungChi"
                    align="center"
                    width={140}
                    render={(v: boolean) =>
                        v ? (
                            <Tag color="green">Đã cấp chứng chỉ</Tag>
                        ) : (
                            <Tag color="red">Chưa có chứng chỉ</Tag>
                        )
                    }
                />
            </Table>
        </div>
    );

    return (
        <section>
            <ContentHeader title="Báo cáo – Thống kê đào tạo" />
            <Tabs
                defaultActiveKey="1"
                items={[
                    /* ══ TAB 1: TỔNG HỢP ═════════════════════════════════════ */
                    {
                        key: '1',
                        label: 'Tổng hợp đào tạo',
                        children: (
                            <>
                                <FilterBar
                                    tuNgay={tuNgay}
                                    denNgay={denNgay}
                                    loading={loading}
                                    onKy={apDungKy}
                                    onRange={(t: any, d: any) => { setTuNgay(t); setDenNgay(d); }}
                                    onReload={() => loadBaoCao(tuNgay, denNgay)}
                                />

                                {/* Stat cards */}
                                <Row gutter={[12, 12]} className="form-group" style={{ marginBottom: 16 }}>
                                    <Col xs={12} sm={12} md={6}>
                                        <StatCard
                                            label="Tổng số lớp"
                                            value={tongHop?.tongSoLop ?? 0}
                                            color="#1890ff"
                                            icon={<BookOutlined rev="" />}
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <StatCard
                                            label="Lớp hoàn thành"
                                            value={tongHop?.tongSoLopHoanThanh ?? 0}
                                            color="#52c41a"
                                            icon={<CheckCircleOutlined rev="" />}
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <StatCard
                                            label="Đang diễn ra"
                                            value={tongHop?.tongSoLopDangDienRa ?? 0}
                                            color="#13c2c2"
                                            icon={<ClockCircleOutlined rev="" />}
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <StatCard
                                            label="Đã khởi tạo"
                                            value={tongHop?.tongSoLopDaKhoiTao ?? (tongHop?.tongSoLopKeHoach ?? 0)}
                                            color="#fa8c16"
                                            icon={<FileTextOutlined rev="" />}
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <StatCard
                                            label="Chưa khởi tạo"
                                            value={tongHop?.tongSoLopChuaKhoiTao ?? 0}
                                            color="#faad14"
                                            icon={<FormOutlined rev="" />}
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <StatCard
                                            label="Tổng học viên"
                                            value={tongHop?.tongSoHocVien ?? 0}
                                            color="#722ed1"
                                            icon={<TeamOutlined rev="" />}
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <StatCard
                                            label="Tỷ lệ hoàn thành"
                                            value={tongHop?.tyLeHoanThanh ?? 0}
                                            color={tongHop?.tyLeHoanThanh >= 80 ? '#52c41a' : (tongHop?.tyLeHoanThanh >= 50 ? '#fa8c16' : '#ff4d4f')}
                                            icon={<TrophyOutlined rev="" />}
                                            suffix="%"
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <StatCard
                                            label="Tổng chi phí đào tạo"
                                            value={currencyFormat.toVND(tongHop?.tongChiPhi ?? 0)}
                                            color="#eb2f96"
                                            icon={<DollarOutlined rev="" />}
                                        />
                                    </Col>
                                </Row>

                                {/* Bảng theo đơn vị */}
                                <div className="card card-primary card-outline" style={{ borderRadius: 8 }}>
                                    <div className="card-header" style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0', padding: '10px 16px' }}>
                                        <h3 className="card-title" style={{ fontSize: 15, fontWeight: 600, color: '#1a3a5c', margin: 0 }}>
                                            <UnorderedListOutlined style={{ marginRight: 8, color: '#1890ff' }} rev="" />
                                            Số lượng người được đào tạo theo phòng ban
                                        </h3>
                                    </div>
                                    <div className="card-body" style={{ padding: '12px 16px' }}>
                                        <Table
                                            dataSource={dsTheoDonVi}
                                            loading={loading}
                                            pagination={{ pageSize: 15, showSizeChanger: false }}
                                            size="small"
                                            locale={{ emptyText: 'Không có dữ liệu' }}
                                        >
                                            <Column title="STT" render={(_: any, __: any, i: number) => i + 1} width={55} />
                                            <Column title="Phòng ban / Đơn vị" dataIndex="tenDonVi" />
                                            <Column title="Số NV đào tạo" dataIndex="soLuongNhanVien" align="center" width={140} />
                                            <Column title="Số lớp" dataIndex="soLuongLopDaoTao" align="center" width={110} />
                                            {/* Tạm ẩn cột chi phí
                                            <Column
                                                title="Chi phí (VND)"
                                                dataIndex="chiPhiDaoTao"
                                                align="right"
                                                width={140}
                                                render={(v: number) => currencyFormat.toVND(v)}
                                            />
                                            */}
                                        </Table>
                                    </div>
                                </div>
                            </>
                        ),
                    },

                    /* ══ TAB 2: CHƯA HOÀN THÀNH ══════════════════════════════ */
                    {
                        key: '2',
                        label: 'Chưa hoàn thành',
                        children: (
                            <>
                                <FilterBar
                                    tuNgay={tuNgay}
                                    denNgay={denNgay}
                                    loading={loading}
                                    onKy={apDungKy}
                                    onRange={(t: any, d: any) => { setTuNgay(t); setDenNgay(d); }}
                                />
                                <div className="card card-primary card-outline" style={{ borderRadius: 8 }}>
                                    <div className="card-header" style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0', padding: '10px 16px' }}>
                                        <h3 className="card-title" style={{ fontSize: 15, fontWeight: 600, color: '#1a3a5c', margin: 0 }}>
                                            <WarningOutlined style={{ marginRight: 8, color: '#ff4d4f' }} rev="" />
                                            Danh sách các lớp đào tạo chưa hoàn thành
                                        </h3>
                                    </div>
                                    <div className="card-body" style={{ padding: '12px 16px' }}>
                                        <Table
                                            dataSource={dsLopChuaHoanThanhGrouped}
                                            loading={loading}
                                            pagination={{ pageSize: 15, showSizeChanger: false }}
                                            size="small"
                                            expandable={{
                                                expandedRowRender: expandedNhanVienChuaHoanThanh,
                                                rowExpandable: (record) => record.dsNhanVien && record.dsNhanVien.length > 0,
                                            }}
                                            locale={{ emptyText: 'Không có lớp đào tạo nào chưa hoàn thành' }}
                                        >
                                            <Column title="STT" render={(_: any, __: any, i: number) => i + 1} width={50} align="center" />
                                            <Column title="Tên lớp đào tạo" dataIndex="tenLopDaoTao" render={(v: string) => <strong>{v}</strong>} />
                                            <Column
                                                title="Ngày bắt đầu"
                                                dataIndex="ngayBatDau"
                                                width={110}
                                                align="center"
                                                render={(v: string) => (v ? dateCommon.formatDate(v) : '-')}
                                            />
                                            <Column
                                                title="Ngày kết thúc"
                                                dataIndex="ngayKetThuc"
                                                width={110}
                                                align="center"
                                                render={(v: string) => (v ? dateCommon.formatDate(v) : '-')}
                                            />
                                            <Column title="Đơn vị / Cơ sở đào tạo" dataIndex="donViDaoTao" render={(v: string) => v || '-'} />
                                            <Column title="Giảng viên" dataIndex="giangVien" render={(v: string) => v || '-'} />
                                            <Column title="Hình thức" dataIndex="tenHinhThucToChuc" render={(v: string) => v || '-'} />
                                            <Column
                                                title="Chưa hoàn thành"
                                                dataIndex="soNhanVienChuaHoanThanh"
                                                align="center"
                                                width={150}
                                                render={(v: number) => (
                                                    <Tag color="volcano" style={{ fontWeight: 600 }}>
                                                        {v} nhân viên
                                                    </Tag>
                                                )}
                                            />
                                            <Column
                                                title="Trạng thái lớp"
                                                dataIndex="trangThaiLop"
                                                align="center"
                                                width={140}
                                                render={(v: number) => <TrangThaiLopDaoTaoTag value={v} />}
                                            />
                                        </Table>
                                    </div>
                                </div>
                            </>
                        ),
                    },

                    /* ══ TAB 3: CHỨNG CHỈ SẮP HẾT HẠN ════════════════════════ */
                    {
                        key: '3',
                        label: 'Chứng chỉ sắp hết hạn',
                        children: (
                            <>
                                <Row className="form-group" gutter={[8, 8]} align="middle">
                                    <Col>
                                        <Space>
                                            <span>Số ngày còn lại ≤</span>
                                            <InputNumber
                                                size="small"
                                                min={1}
                                                max={365}
                                                value={soNgayChungChi}
                                                onChange={(v) => setSoNgayChungChi(v || 30)}
                                            />
                                            <span>ngày</span>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<SearchOutlined rev="" />}
                                                onClick={loadChungChi}
                                                loading={loadingChungChi}
                                            >
                                                Tìm kiếm
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                                <div className="card card-primary card-outline" style={{ borderRadius: 8 }}>
                                    <div className="card-header" style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0', padding: '10px 16px' }}>
                                        <h3 className="card-title" style={{ fontSize: 15, fontWeight: 600, color: '#1a3a5c', margin: 0 }}>
                                            <WarningOutlined style={{ marginRight: 8, color: '#fa8c16' }} rev="" />
                                            Danh sách chứng chỉ sắp hết hạn
                                        </h3>
                                    </div>
                                    <div className="card-body" style={{ padding: '12px 16px' }}>
                                        <Table
                                            dataSource={dsChungChiSapHetHan}
                                            loading={loadingChungChi}
                                            pagination={{ pageSize: 20, showSizeChanger: false }}
                                            size="small"
                                            locale={{ emptyText: 'Không có dữ liệu' }}
                                        >
                                            <Column title="STT" render={(_: any, __: any, i: number) => i + 1} width={55} />
                                            <Column title="Họ tên" dataIndex="hoTen" />
                                            <Column title="Phòng ban" dataIndex="tenDonVi" />
                                            <Column title="Tên chứng chỉ" dataIndex="tenChungChi" />
                                            <Column title="Loại" dataIndex="loaiChungChi" width={110} />
                                            <Column title="Đơn vị cấp" dataIndex="donViCap" />
                                            <Column
                                                title="Ngày cấp"
                                                dataIndex="ngayCapChungChi"
                                                width={100}
                                                render={(v: string) => (v ? dateCommon.formatDate(v) : '')}
                                            />
                                            <Column
                                                title="Hết hạn"
                                                dataIndex="apDungDenNgay"
                                                width={100}
                                                render={(v: string) => (v ? dateCommon.formatDate(v) : '')}
                                            />
                                            <Column
                                                title="Còn lại"
                                                dataIndex="soNgayConLai"
                                                align="center"
                                                width={95}
                                                render={(v: number) => (
                                                    <Tag
                                                        color={v <= 7 ? 'red' : v <= 14 ? 'orange' : 'gold'}
                                                        icon={<WarningOutlined rev="" />}
                                                    >
                                                        {v} ngày
                                                    </Tag>
                                                )}
                                            />
                                        </Table>
                                    </div>
                                </div>
                            </>
                        ),
                    },

                    /* ══ TAB 4: LỊCH SỬ ĐÀO TẠO ══════════════════════════════ */
                    {
                        key: '4',
                        label: 'Lịch sử đào tạo',
                        children: (
                            <>
                                <Row className="form-group" gutter={[8, 8]} align="middle">
                                    <Col span={8}>
                                        <Input
                                            size="small"
                                            prefix={<SearchOutlined rev="" />}
                                            placeholder="Nhập tên nhân viên hoặc tên lớp đào tạo"
                                            value={tuKhoaLichSu}
                                            onChange={(e) => setTuKhoaLichSu(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') loadLichSu();
                                            }}
                                        />
                                    </Col>
                                    <Col>
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<SearchOutlined rev="" />}
                                            onClick={loadLichSu}
                                            loading={loadingLichSu}
                                        >
                                            Tìm kiếm
                                        </Button>
                                    </Col>
                                </Row>
                                <div className="card card-primary card-outline" style={{ borderRadius: 8 }}>
                                    <div className="card-header" style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0', padding: '10px 16px' }}>
                                        <h3 className="card-title" style={{ fontSize: 15, fontWeight: 600, color: '#1a3a5c', margin: 0 }}>
                                            <HistoryOutlined style={{ marginRight: 8, color: '#1890ff' }} rev="" />
                                            Lịch sử đào tạo của từng người lao động
                                        </h3>
                                    </div>
                                    <div className="card-body" style={{ padding: '12px 16px' }}>
                                        <Table
                                            dataSource={dsLichSuGrouped}
                                            loading={loadingLichSu}
                                            pagination={{ pageSize: 20, showSizeChanger: false }}
                                            size="small"
                                            locale={{ emptyText: 'Không có dữ liệu' }}
                                            expandable={{
                                                expandedRowRender: expandedLopDaoTao,
                                                expandedRowKeys: expandedLichSu,
                                                onExpand: (expanded, record) => {
                                                    if (expanded) {
                                                        setExpandedLichSu((prev) => [...prev, record.key]);
                                                    } else {
                                                        setExpandedLichSu((prev) =>
                                                            prev.filter((k) => k !== record.key)
                                                        );
                                                    }
                                                },
                                            }}
                                        >
                                            <Column title="STT" render={(_: any, __: any, i: number) => i + 1} width={55} />
                                            <Column title="Họ tên" dataIndex="hoTen" render={(text: string) => <span style={{ fontWeight: 600 }}>{text}</span>} />
                                            <Column title="Nhóm nhân sự" dataIndex="tenNhomNhanSu" width={140} />
                                            <Column title="Phòng ban" dataIndex="tenDonVi" />
                                            <Column title="Chức vụ" dataIndex="tenChucVu" width={150} />
                                            <Column
                                                title="Số lớp tham gia"
                                                dataIndex="soLopThamGia"
                                                align="center"
                                                width={130}
                                                render={(v: number) => (
                                                    <Tag color="blue">{v} lớp</Tag>
                                                )}
                                            />
                                        </Table>
                                    </div>
                                </div>
                            </>
                        ),
                    },
                ]}
            />
        </section>
    );
};

export default BaoCaoDaoTao;
